import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RenderRequest, PreviewHoverPayload, PreviewExportHandle, ExportAction, ExportResult } from 'types/apis/cxEditor';
import { captureIframeContentToPng, captureElementToPng, buildExportFilename, performExport } from 'utils/preview-export';
import { Box, Typography, useTheme, Card, CardHeader, Button, CircularProgress } from '@mui/material';
import { useWidgetSdk } from 'context/WidgetSdkContext';
import { WidgetErrorBoundary } from './WidgetErrorBoundary';
import { MobilePreview } from './MobilePreview';
import { WebWidgetMobileIframe } from './WebWidgetMobileIframe';
import { initializeAndRenderWidget, initializeAndRenderMultipleWidgets } from 'utils/widget-renderer';
import { getWidgetIdFromTemplate, getWebWidgetDesktopWidth, getWebWidgetDisplayName, isMultiWidgetTemplate } from 'constants/web-widget-mapping';
import {
  getDeepestElementFromPoint,
  getNonPopupElementFromPoint,
  isWithinBoundary,
  getElementTextColorHex,
  resolveElementBackgroundHex,
  extractDataAttrHints,
} from 'utils/preview-hover-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store/Store';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { selectScenariosForTemplate, selectDataScenariosLoading } from 'store/dataReducers/dataScenariosSlice';
import { toast } from 'utils/toast';

/** iPhone 17 Pro Max frame (440×956 viewport, 14px bezel). */
const MOBILE_PHONE = { width: 440, height: 956, bezel: 14 } as const;

/** Max scale so the phone never looks oversized on large screens (more like a real iPhone). */
const MAX_PHONE_SCALE = 0.7;

/** How long (ms) the mouse must dwell on an element before hover detection triggers. */
const HOVER_DWELL_MS = 500;

/** Mobile viewport CSS: forces entire page to fit inside device frame, prevents horizontal overflow. */
const EMAIL_MOBILE_CSS = `
  *, *::before, *::after { box-sizing: border-box !important; }
  html {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: 100%;
    margin: 0;
    padding: 0;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    -webkit-text-size-adjust: 100%;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }
  body {
    margin: 0 !important;
    padding: 0 !important;
    padding-top: 44px !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    min-height: 100%;
    overflow-x: hidden !important;
    overflow-y: visible;
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    word-break: break-word;
    /* Do not set font-family: email inline styles (Arial, Poppins, etc.) must win to avoid different line breaks */
  }
  body > * {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  img {
    max-width: 100% !important;
    width: auto !important;
    height: auto !important;
    display: block;
  }
  table, table[width] {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
  }
  td, th, td[width], th[width] {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    word-break: break-word;
  }
  .wrapper, .outer, .webkit, .email-container, .content,
  center[class="wrapper"], div[class="webkit"], table[class="outer"],
  table[class="wrapper"], div[class="outer"], div[class="wrapper"],
  [class*="wrapper"], [class*="container"], [class*="content"],
  table[width], td[width], th[width] {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  pre, code {
    max-width: 100% !important;
    overflow-x: auto !important;
    white-space: pre-wrap !important;
    word-wrap: break-word !important;
  }
  a, button, input, select, textarea {
    max-width: 100% !important;
  }
  div, section, article, main, aside, header, footer, span, p {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  [style*="flex"], [style*="display: flex"], .flex, [class*="flex"] {
    flex-wrap: wrap !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  [style*="flex"] > *, [style*="display: flex"] > *, .flex > *, [class*="flex"] > * {
    min-width: 0 !important;
    flex-shrink: 1 !important;
  }

  /* User Feedback (FEEDBACK_V4, FEEDBACK_V3, FEEDBACK): keep question + icons on same line */
  tr[id$="FEEDBACK_V4"] div,
  tr[id$="FEEDBACK_V4"] table,
  tr[id$="FEEDBACK_V3"] div,
  tr[id$="FEEDBACK_V3"] table,
  tr[id$="FEEDBACK"] div,
  tr[id$="FEEDBACK"] table {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  tr[id$="FEEDBACK_V4"] tr,
  tr[id$="FEEDBACK_V3"] tr,
  tr[id$="FEEDBACK"] tr {
    display: table-row !important;
  }
  tr[id$="FEEDBACK_V4"] td,
  tr[id$="FEEDBACK_V3"] td,
  tr[id$="FEEDBACK"] td {
    display: table-cell !important;
    vertical-align: middle !important;
    box-sizing: border-box !important;
    padding: 12px 16px 0 !important;
  }
  tr[id$="FEEDBACK_V4"] td:first-of-type,
  tr[id$="FEEDBACK_V3"] td:first-of-type,
  tr[id$="FEEDBACK"] td:first-of-type {
    white-space: normal !important;
    width: auto !important;
    min-width: 0 !important;
  }
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type),
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type),
  tr[id$="FEEDBACK"] td:not(:first-of-type) {
    white-space: nowrap !important;
    width: 110px !important;
    max-width: 110px !important;
  }
  tr[id$="FEEDBACK_V4"] div,
  tr[id$="FEEDBACK_V3"] div,
  tr[id$="FEEDBACK"] div {
    padding-left: 16px !important;
    padding-bottom: 12px !important;
  }
  /* Thumbs icons: prevent clipping, ensure full display */
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type) img,
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type) img,
  tr[id$="FEEDBACK"] td:not(:first-of-type) img {
    display: inline-block !important;
    vertical-align: middle !important;
    object-fit: contain !important;
    width: 24px !important;
    height: 24px !important;
    max-width: 24px !important;
    max-height: 24px !important;
  }
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type),
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type),
  tr[id$="FEEDBACK"] td:not(:first-of-type) {
    overflow: visible !important;
    min-height: 46px !important;
  }

  /* Footer (FOOTER, FOOTER_V3, FOOTER_V4, NEW_FOOTER): prevent overflow, reduce extra space */
  tr[id$="FOOTER"] table,
  tr[id$="FOOTER_V3"] table,
  tr[id$="FOOTER_V4"] table,
  tr[id$="NEW_FOOTER"] table {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  tr[id$="FOOTER"] > td,
  tr[id$="FOOTER_V3"] > td,
  tr[id$="FOOTER_V4"] > td,
  tr[id$="NEW_FOOTER"] > td {
    max-width: 100% !important;
    box-sizing: border-box !important;
    padding: 20px 16px 16px !important;
    height: auto !important;
  }
  tr[id$="FOOTER"] .content-block-footer,
  tr[id$="FOOTER_V3"] .content-block-footer,
  tr[id$="FOOTER_V4"] .content-block-footer,
  tr[id$="NEW_FOOTER"] .content-block-footer {
    padding-top: 20px !important;
    padding-bottom: 16px !important;
  }
  tr[id$="FOOTER"] .table-not-adapt,
  tr[id$="FOOTER"] table[class*="social"],
  tr[id$="FOOTER_V3"] .table-not-adapt,
  tr[id$="FOOTER_V3"] table[class*="social"],
  tr[id$="FOOTER_V4"] .table-not-adapt,
  tr[id$="FOOTER_V4"] table[class*="social"],
  tr[id$="NEW_FOOTER"] .table-not-adapt,
  tr[id$="NEW_FOOTER"] table[class*="social"] {
    margin: 16px 0 !important;
  }

  /* Bill Summary (MONTHLY_SUMMARY_V2, MONTHLY_SUMMARY): align arrow with main amount */
  tr[id$="MONTHLY_SUMMARY_V2"] table tr td,
  tr[id$="MONTHLY_SUMMARY"] table tr td {
    vertical-align: bottom !important;
  }
  /* Bill Summary: keep amount column (and green row) on one line — target inner table where amounts live */
  tr[id$="MONTHLY_SUMMARY_V2"] table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY"] table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY_V2"] table table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY"] table table tr td:last-child {
    white-space: nowrap !important;
  }
  /* All section tables: prevent numbers from breaking mid-digit (Bill Summary, Itemization, etc.) */
  tr[id] table td,
  tr[id] table th {
    word-break: normal !important;
    overflow-wrap: normal !important;
  }
  tr[id$="MONTHLY_SUMMARY_V2"] img[src*="Up_down"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="arrow"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="Up"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="Down"],
  tr[id$="MONTHLY_SUMMARY"] img[src*="Up_down"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="arrow"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="Up"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="Down"] {
    vertical-align: middle !important;
    display: inline-block !important;
  }

  /* All section rows: ensure nested tables/divs respect viewport (safety for HEADER, GREETING, MONTHLY_SUMMARY, ITEMIZATION, etc.) */
  tr[id] table,
  tr[id] div {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

class EmailPreviewErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[EmailPreviewErrorBoundary] Email render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ padding: 3, textAlign: 'center', bgcolor: 'error.light', borderRadius: 2, border: 1, borderColor: 'error.main' }}>
          <Typography variant="h6" color="error" gutterBottom>Email Preview Error</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {this.state.error?.message || 'An error occurred while rendering the email preview'}
          </Typography>
          <Button variant="outlined" color="error" onClick={() => this.setState({ hasError: false, error: null })}>
            Try Again
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

/**
 * Builds a full HTML document string for rendering email content in an iframe
 * for desktop preview. Passes through the full document as-is so the email's
 * own <style> tags and inline font-family declarations are preserved.
 * Intentionally adds NO font or layout overrides so the rendering matches
 * production email clients (avoids the global `* { font-family: Inter }` rule
 * from index.css leaking in through dangerouslySetInnerHTML).
 */
function buildEmailDesktopDocument(html: string): string {
  if (!html?.trim()) {
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body></body></html>`;
  }
  const trimmed = html.trim();
  const looksLikeFullDocument =
    trimmed.toLowerCase().startsWith('<!doctype') || trimmed.toLowerCase().startsWith('<html');
  // For full documents pass through as-is. The iframe srcdoc is already sandboxed so
  // the app's global CSS cannot bleed in — there is no reason to reconstruct the document.
  // Reconstructing with DOMParser drops <html>/<body> attributes, MSO conditional comments,
  // non-style <meta> tags, and overwrites original body styles, all of which break email rendering.
  if (looksLikeFullDocument) {
    return html;
  }
  // Fragment: wrap in a minimal shell.
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body style="margin:0;padding:0;">${html}</body></html>`;
}

function extractEmailTeaserText(html: string): string | null {
  if (!html?.trim()) return null;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nodes = Array.from(doc.querySelectorAll('div, span, p, td')).reverse();

    for (const el of nodes) {
      const style = (el.getAttribute('style') || '').replace(/\s+/g, '').toLowerCase();
      const isHidden =
        style.includes('mso-hide:all') ||
        style.includes('display:none') ||
        style.includes('visibility:hidden') ||
        style.includes('max-height:0') ||
        style.includes('max-width:0') ||
        style.includes('font-size:0') ||
        style.includes('line-height:0') ||
        style.includes('opacity:0');

      if (!isHidden) continue;

      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
  } catch {
    return null;
  }

  return null;
}

/** Desktop email preview: auto-height iframe so global app CSS cannot affect email font rendering. */
const EmailDesktopPreview: React.FC<{
  html: string;
  channel?: 'Email' | 'Web' | 'HER';
  onPreviewHover?: (payload: PreviewHoverPayload | null) => void;
  onPreviewHoverPin?: (payload: PreviewHoverPayload) => void;
  hoverInvestigateEnabled?: boolean;
  showColorTooltip?: boolean;
  captureRef?: React.RefObject<(() => Promise<Blob | null>) | null>;
  fetchImage?: (src: string) => Promise<string | null>;
}> = ({ html, channel = 'Email', onPreviewHover, onPreviewHoverPin, hoverInvestigateEnabled = false, showColorTooltip = false, captureRef, fetchImage }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  React.useEffect(() => {
    if (!captureRef) return;
    captureRef.current = async () => {
      const iframe = iframeRef.current;
      if (!iframe) return null;
      try { return await captureIframeContentToPng(iframe, fetchImage); } catch { return null; }
    };
    return () => { captureRef.current = null; };
  }, [captureRef, fetchImage]);
  const cleanupRef = React.useRef<(() => void) | null>(null);
  const [iframeSize, setIframeSize] = React.useState({ width: 1024, height: 600 });
  const [scale, setScale] = React.useState(1);
  const [iframeKey, setIframeKey] = React.useState(0);

  const srcdoc = React.useMemo(() => buildEmailDesktopDocument(html), [html]);

  const measureIframe = React.useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    const root = doc?.documentElement;
    const body = doc?.body;
    if (!root || !body) return;

    // Width: use the wider of root and body (email max-width containers sit on root).
    // Height: use body only — root.scrollHeight/clientHeight equals the iframe's current
    // viewport height (set via iframeSize.height), so including it would prevent the
    // height from ever shrinking below its previous value.
    const width = Math.max(
      root.scrollWidth,
      root.offsetWidth,
      root.clientWidth,
      body.scrollWidth,
      body.offsetWidth,
      body.clientWidth,
      1
    );
    const height = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      body.clientHeight,
      1
    );

    setIframeSize(prev =>
      prev.width === width && prev.height === height ? prev : { width, height }
    );
  }, []);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateScale = () => {
      const nextScale = Math.min(1, el.clientWidth / Math.max(iframeSize.width, 1));
      setScale(prev => (Math.abs(prev - nextScale) < 0.001 ? prev : nextScale));
    };

    updateScale();

    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScale);
    });
    ro.observe(el);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [iframeSize.width]);

  React.useEffect(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    setIframeSize({ width: 1024, height: 600 }); // Reset so new (possibly smaller) content measures correctly
    setIframeKey(k => k + 1); // Force iframe remount on new content so stale DOM state is discarded
  }, [srcdoc]);

  React.useEffect(() => {
    return () => cleanupRef.current?.();
  }, []);

  // Keep stable refs so the handleLoad callback doesn't need to depend on
  // these props (which would cause re-attach on every render).
  const onPreviewHoverRef = React.useRef(onPreviewHover);
  React.useEffect(() => { onPreviewHoverRef.current = onPreviewHover; }, [onPreviewHover]);
  const onPreviewHoverPinRef = React.useRef(onPreviewHoverPin);
  React.useEffect(() => { onPreviewHoverPinRef.current = onPreviewHoverPin; }, [onPreviewHoverPin]);
  // Track channel via ref so the hover listener closure (attached once per
  // iframe load) always emits the current channel without needing reattach.
  const channelRef = React.useRef(channel);
  React.useEffect(() => { channelRef.current = channel; }, [channel]);
  const hoverInvestigateEnabledRef = React.useRef(hoverInvestigateEnabled);
  React.useEffect(() => {
    hoverInvestigateEnabledRef.current = hoverInvestigateEnabled;
    if (!hoverInvestigateEnabled) {
      onPreviewHoverRef.current?.(null);
    }
  }, [hoverInvestigateEnabled]);
  const showColorTooltipRef = React.useRef(showColorTooltip);
  React.useEffect(() => { showColorTooltipRef.current = showColorTooltip; }, [showColorTooltip]);

  const handleLoad = React.useCallback(() => {
    cleanupRef.current?.();

    const iframe = iframeRef.current;
    const win = iframe?.contentWindow;
    const doc = iframe?.contentDocument;
    const root = doc?.documentElement;
    const body = doc?.body;

    if (!win || !doc || !root || !body) {
      measureIframe();
      return;
    }

    let rafId = 0;
    const scheduleMeasure = () => {
      win.cancelAnimationFrame(rafId);
      rafId = win.requestAnimationFrame(() => {
        measureIframe();
      });
    };

    scheduleMeasure();

    // Avoid ResizeObserver inside the iframe document: resizing the parent iframe in response
    // to observer callbacks can still create a feedback cycle and trigger the dev overlay
    // warning "ResizeObserver loop completed with undelivered notifications".
    // For email previews, observing DOM mutations plus image/font completion is enough.
    const mutationObserver =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(scheduleMeasure) : null;
    mutationObserver?.observe(body, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    const images = Array.from(doc.images);
    images.forEach(image => {
      image.addEventListener('load', scheduleMeasure);
      image.addEventListener('error', scheduleMeasure);
    });

    doc.fonts?.ready.then(scheduleMeasure).catch(() => {});
    win.setTimeout(scheduleMeasure, 0);

    // Prevent anchor clicks from navigating the iframe (e.g. images wrapped in <a> tags
    // would otherwise load the app URL inside the preview iframe).
    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as Element)?.closest('a');
      if (anchor) e.preventDefault();
    };
    body.addEventListener('click', handleClick);

    // --- Hover capture for preview-to-inspector matching ---
    console.log('[EmailDesktopPreview] handleLoad: attaching hover listeners, onPreviewHoverRef:', !!onPreviewHoverRef.current, 'showColorTooltipRef:', showColorTooltipRef.current);
    if (!hoverInvestigateEnabledRef.current) {
      cleanupRef.current = () => {
        win.cancelAnimationFrame(rafId);
        mutationObserver?.disconnect();
        images.forEach(image => {
          image.removeEventListener('load', scheduleMeasure);
          image.removeEventListener('error', scheduleMeasure);
        });
        body.removeEventListener('click', handleClick);
        onPreviewHoverRef.current?.(null);
      };
      return;
    }
    let hoverDwellTimer: ReturnType<typeof setTimeout> | null = null;
    let hoverThrottleTimer: ReturnType<typeof setTimeout> | null = null;
    let chipHideTimer: ReturnType<typeof setTimeout> | null = null;
    // Track the last emitted element (not just text) so that moving between
    // two different elements that share identical copy (e.g. two "Learn more"
    // links) still emits a fresh payload — their color, background, and
    // context may differ and are needed for disambiguation in the inspector.
    let lastEmittedEl: Element | null = null;
    let pendingEl: Element | null = null;
    let currentHlEl: Element | null = null;

    // Create a tooltip element inside the iframe for showing color hex
    const tooltip = doc.createElement('div');
    tooltip.style.cssText =
      'position:fixed;z-index:999999;pointer-events:none;display:none;' +
      'padding:3px 8px;border-radius:4px;font-size:11px;font-family:monospace;' +
      'background:rgba(30,30,30,.88);color:#fff;white-space:nowrap;line-height:1.4;' +
      'box-shadow:0 2px 6px rgba(0,0,0,.25);';
    body.appendChild(tooltip);

    // Block-highlight style: outlines the hovered element like browser DevTools
    const hlStyle = doc.createElement('style');
    hlStyle.textContent = '.quill-hover-hl{outline:2px solid rgba(59,130,246,.8)!important;outline-offset:1px!important;background-color:rgba(59,130,246,.06)!important;}';
    body.appendChild(hlStyle);

    // "Fix it" chip — tooltip-style, shows on hover of a matched element; click pins inspector highlight
    const fixItChip = doc.createElement('div');
    fixItChip.style.cssText = [
      'position:fixed', 'z-index:1000001', 'pointer-events:auto', 'display:none',
      'padding:4px 10px', 'border-radius:4px', 'font-size:11px',
      'font-family:-apple-system,BlinkMacSystemFont,sans-serif',
      'background:rgba(33,33,33,0.88)', 'color:#fff', 'cursor:pointer',
      'box-shadow:0 2px 8px rgba(0,0,0,.32)', 'user-select:none', 'line-height:1.5',
      'white-space:nowrap', 'transition:background 0.12s,color 0.12s',
    ].join(';');
    fixItChip.appendChild(doc.createTextNode('Fix it'));
    const chipArrow = doc.createElement('div');
    chipArrow.style.cssText = 'position:absolute;bottom:-5px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid rgba(33,33,33,0.88);pointer-events:none';
    fixItChip.appendChild(chipArrow);
    body.appendChild(fixItChip);
    let currentChipPayload: PreviewHoverPayload | null = null;

    const showChip = (el: Element, payload: PreviewHoverPayload) => {
      currentChipPayload = payload;
      fixItChip.style.background = 'rgba(33,33,33,0.88)';
      fixItChip.style.color = '#fff';
      chipArrow.style.display = 'none';
      fixItChip.style.display = 'block';
      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const cw = fixItChip.offsetWidth || 60;
        const ch = fixItChip.offsetHeight || 26;
        // Anchor chip inside element at top-right corner so cursor never leaves element bounds while reaching chip
        const top = Math.min(Math.max(4, rect.top + 4), (win.innerHeight || 600) - ch - 4);
        const left = Math.min(Math.max(4, rect.right - cw - 4), (win.innerWidth || 800) - cw - 4);
        fixItChip.style.top = `${top}px`;
        fixItChip.style.left = `${left}px`;
      });
    };
    const hideChip = (delay = 0) => {
      if (chipHideTimer) { clearTimeout(chipHideTimer); chipHideTimer = null; }
      if (delay > 0) {
        chipHideTimer = setTimeout(() => { chipHideTimer = null; fixItChip.style.display = 'none'; }, delay);
      } else {
        fixItChip.style.display = 'none';
      }
    };
    fixItChip.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation();
      if (currentChipPayload) onPreviewHoverPinRef.current?.(currentChipPayload);
      fixItChip.style.background = 'transparent';
      fixItChip.style.color = 'transparent';
      chipArrow.style.borderTopColor = 'transparent';
      chipArrow.style.borderBottomColor = 'transparent';
      setTimeout(() => hideChip(), 180);
    });

    const showTooltip = (e: MouseEvent, colorHex: string | null) => {
      if (!colorHex || !showColorTooltipRef.current) { tooltip.style.display = 'none'; return; }
      // Build tooltip: color swatch + hex value
      tooltip.innerHTML =
        `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;border:1px solid rgba(255,255,255,.4);background:${colorHex};vertical-align:middle;margin-right:5px;"></span>` +
        colorHex.toUpperCase();
      tooltip.style.display = 'block';
      // Position near the cursor
      const x = Math.min(e.clientX + 12, (win.innerWidth || 800) - 120);
      const y = Math.max(e.clientY - 28, 4);
      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y}px`;
    };

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || target === fixItChip || target === chipArrow || target === tooltip) {
        // Mouse entered chip zone — cancel any pending delayed hide so chip stays clickable
        if (chipHideTimer) { clearTimeout(chipHideTimer); chipHideTimer = null; }
        return;
      }

      // Block highlight: outline the exact element under cursor (DevTools-style)
      if (target !== currentHlEl && target !== body) {
        currentHlEl?.classList.remove('quill-hover-hl');
        target.classList.add('quill-hover-hl');
        currentHlEl = target;
      }

      const colorHex = getElementTextColorHex(target, win);
      showTooltip(e, colorHex);

      // Always cancel dwell for whichever element it was tracking — prevents stale
      // timer firing after A→B→A navigation where user returns before B's dwell ends.
      if (hoverDwellTimer) { clearTimeout(hoverDwellTimer); hoverDwellTimer = null; }

      if (target === lastEmittedEl) {
        pendingEl = null;
        // Re-show chip if user returned to previously matched element
        if (currentChipPayload && fixItChip.style.display === 'none') showChip(target, currentChipPayload);
        return;
      }

      const hints = extractDataAttrHints(target, body);
      if (!hints) { hideChip(150); return; }

      hideChip(150); // delayed hide — gives user time to reach chip before it disappears

      hoverDwellTimer = setTimeout(() => {
        hoverDwellTimer = null;

        if (hoverThrottleTimer) {
          pendingEl = target;
          return;
        }

        lastEmittedEl = target;
        pendingEl = null;

        if (!onPreviewHoverRef.current) return;

        const currentChannel = channelRef.current;
        const payload: PreviewHoverPayload = {
          channel: currentChannel,
          colorHex,
          backgroundHex: resolveElementBackgroundHex(target, body, win),
          source: currentChannel === 'Web' ? 'web-desktop' : currentChannel === 'HER' ? 'her-desktop' : 'email-desktop',
          dataAttrHints: hints,
        };
        onPreviewHoverRef.current(payload);
        showChip(target, payload);

        hoverThrottleTimer = setTimeout(() => {
          hoverThrottleTimer = null;
          const el = pendingEl;
          pendingEl = null;
          if (!el || el === lastEmittedEl || !onPreviewHoverRef.current) return;
          const pendingHints = extractDataAttrHints(el, body);
          if (!pendingHints) return;
          lastEmittedEl = el;
          const ch = channelRef.current;
          const pendingPayload: PreviewHoverPayload = {
            channel: ch,
            colorHex: getElementTextColorHex(el, win),
            backgroundHex: resolveElementBackgroundHex(el, body, win),
            source: ch === 'Web' ? 'web-desktop' : ch === 'HER' ? 'her-desktop' : 'email-desktop',
            dataAttrHints: pendingHints,
          };
          onPreviewHoverRef.current(pendingPayload);
          showChip(el, pendingPayload);
        }, 50);
      }, HOVER_DWELL_MS);
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Keep tooltip tracking the cursor
      if (tooltip.style.display === 'block') {
        const x = Math.min(e.clientX + 12, (win.innerWidth || 800) - 120);
        const y = Math.max(e.clientY - 28, 4);
        tooltip.style.left = `${x}px`;
        tooltip.style.top = `${y}px`;
      }
    };

    const handleMouseLeave = () => {
      currentHlEl?.classList.remove('quill-hover-hl');
      currentHlEl = null;
      lastEmittedEl = null;
      pendingEl = null;
      if (hoverDwellTimer) { clearTimeout(hoverDwellTimer); hoverDwellTimer = null; }
      if (hoverThrottleTimer) { clearTimeout(hoverThrottleTimer); hoverThrottleTimer = null; }
      if (chipHideTimer) { clearTimeout(chipHideTimer); chipHideTimer = null; }
      tooltip.style.display = 'none';
      currentChipPayload = null;
      hideChip();
      onPreviewHoverRef.current?.(null);
    };

    body.addEventListener('mouseover', handleMouseOver);
    body.addEventListener('mousemove', handleMouseMove);
    body.addEventListener('mouseleave', handleMouseLeave);

    cleanupRef.current = () => {
      win.cancelAnimationFrame(rafId);
      mutationObserver?.disconnect();
      images.forEach(image => {
        image.removeEventListener('load', scheduleMeasure);
        image.removeEventListener('error', scheduleMeasure);
      });
      body.removeEventListener('mouseover', handleMouseOver);
      body.removeEventListener('mousemove', handleMouseMove);
      body.removeEventListener('mouseleave', handleMouseLeave);
      body.removeEventListener('click', handleClick);
      if (hoverDwellTimer) clearTimeout(hoverDwellTimer);
      if (hoverThrottleTimer) clearTimeout(hoverThrottleTimer);
      if (chipHideTimer) clearTimeout(chipHideTimer);
      currentHlEl?.classList.remove('quill-hover-hl');
      currentHlEl = null;
      tooltip.remove();
      hlStyle.remove();
      fixItChip.remove();
      currentChipPayload = null;
      hideChip();
      lastEmittedEl = null;
      onPreviewHoverRef.current?.(null);
    };
  }, [measureIframe]);

  React.useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) return;
    handleLoad();
  }, [handleLoad, hoverInvestigateEnabled, iframeKey]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: `${iframeSize.width * scale}px`,
          height: `${iframeSize.height * scale}px`,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <iframe
          key={iframeKey}
          ref={iframeRef}
          title="Email desktop preview"
          srcDoc={srcdoc}
          onLoad={handleLoad}
          style={{
            width: `${iframeSize.width}px`,
            height: `${iframeSize.height}px`,
            border: 'none',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            backgroundColor: '#ffffff',
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * HER desktop preview.
 *
 * HER preview HTML is served cross-origin normally, but we now render it via
 * `srcDoc` (see `prepareHerSrcDoc` in `utils/her-preview-url`) which makes the
 * iframe same-origin and lets us inspect its DOM. This component mirrors the
 * hover pipeline of `EmailDesktopPreview` but is kept as its own component so
 * HER can evolve independently of Email (e.g. no auto-height measuring — HER
 * reports render inside a fixed-height scroll area rather than an expanding
 * iframe).
 */
// Width (px) at which proxypulse captures the HER snapshot. The iframe is
// rendered at this fixed width and scaled down to fit the container so that
// CSS breakpoints match the capture context exactly.
const HER_CAPTURE_WIDTH = 1280;

const HerDesktopPreview: React.FC<{
  srcDoc: string;
  onPreviewHover?: (payload: PreviewHoverPayload | null) => void;
  onPreviewHoverPin?: (payload: PreviewHoverPayload) => void;
  hoverInvestigateEnabled?: boolean;
  showColorTooltip?: boolean;
  captureRef?: React.RefObject<(() => Promise<Blob | null>) | null>;
  fetchImage?: (src: string) => Promise<string | null>;
}> = ({ srcDoc, onPreviewHover, onPreviewHoverPin, hoverInvestigateEnabled = false, showColorTooltip = false, captureRef, fetchImage }) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const cleanupRef = React.useRef<(() => void) | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [iframeHeight, setIframeHeight] = React.useState(900);
  // Width of the actual report content (.her-container). The container is
  // shrink-wrapped via preview-only CSS (see HER_PREVIEW_OVERRIDE_CSS in
  // utils/her-preview-url), so scaling to this width crops the empty filler
  // region that the real HER page shows right of the report.
  const [contentWidth, setContentWidth] = React.useState(HER_CAPTURE_WIDTH);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setContainerWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!captureRef) return;
    captureRef.current = async () => {
      const iframe = iframeRef.current;
      if (!iframe) return null;
      // Capture the shrink-wrapped report container so exported PNGs are
      // cropped to the report width; fall back to the full document.
      const doc = iframe.contentDocument;
      const target = doc?.querySelector('.her-container') ?? doc?.documentElement;
      if (!target) return null;
      try { return await captureElementToPng(target, fetchImage); } catch { return null; }
    };
    return () => { captureRef.current = null; };
  }, [captureRef, fetchImage]);
  const [iframeKey, setIframeKey] = React.useState(0);

  // Stable refs so the load handler (attached once per iframe mount) reads the
  // latest props without needing to be re-created on every render.
  const onPreviewHoverRef = React.useRef(onPreviewHover);
  React.useEffect(() => { onPreviewHoverRef.current = onPreviewHover; }, [onPreviewHover]);
  const onPreviewHoverPinRef = React.useRef(onPreviewHoverPin);
  React.useEffect(() => { onPreviewHoverPinRef.current = onPreviewHoverPin; }, [onPreviewHoverPin]);
  const hoverInvestigateEnabledRef = React.useRef(hoverInvestigateEnabled);
  React.useEffect(() => {
    hoverInvestigateEnabledRef.current = hoverInvestigateEnabled;
    if (!hoverInvestigateEnabled) {
      onPreviewHoverRef.current?.(null);
    }
  }, [hoverInvestigateEnabled]);
  const showColorTooltipRef = React.useRef(showColorTooltip);
  React.useEffect(() => { showColorTooltipRef.current = showColorTooltip; }, [showColorTooltip]);

  // Remount iframe when content changes so stale hover state (last emitted
  // element, tooltip DOM) doesn't leak across scenario switches.
  React.useEffect(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    setContentWidth(HER_CAPTURE_WIDTH);
    setIframeKey(k => k + 1);
  }, [srcDoc]);

  React.useEffect(() => {
    return () => cleanupRef.current?.();
  }, []);

  const handleLoad = React.useCallback(() => {
    cleanupRef.current?.();

    const iframe = iframeRef.current;
    const win = iframe?.contentWindow;
    const doc = iframe?.contentDocument;
    const body = doc?.body;
    if (!win || !doc || !body) return;

    // Measure content height so the outer container scales correctly.
    const contentHeight = doc.documentElement.scrollHeight || body.scrollHeight;
    if (contentHeight > 0) setIframeHeight(contentHeight);

    // Measure the report container width so the preview scales/crops to the
    // report itself rather than the full 1280px capture width. Use the right
    // edge so a left offset can't clip content.
    const herEl = doc.querySelector('.her-container');
    const herRect = herEl?.getBoundingClientRect();
    if (herRect && herRect.right > 0) {
      setContentWidth(Math.min(HER_CAPTURE_WIDTH, Math.ceil(herRect.right)));
    }

    // Prevent anchor clicks from navigating the iframe (e.g. images wrapped in <a> tags
    // would otherwise load the app URL inside the preview iframe).
    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as Element)?.closest('a');
      if (anchor) e.preventDefault();
    };
    body.addEventListener('click', handleClick);

    if (!hoverInvestigateEnabledRef.current) {
      cleanupRef.current = () => {
        body.removeEventListener('click', handleClick);
        onPreviewHoverRef.current?.(null);
      };
      return;
    }

    let hoverDwellTimer: ReturnType<typeof setTimeout> | null = null;
    let hoverThrottleTimer: ReturnType<typeof setTimeout> | null = null;
    let chipHideTimer: ReturnType<typeof setTimeout> | null = null;
    let lastEmittedEl: Element | null = null;
    let pendingEl: Element | null = null;
    let currentHlEl: Element | null = null;

    // Tooltip for color-hex hint (matches Email/Web tooltip styling).
    const tooltip = doc.createElement('div');
    tooltip.style.cssText =
      'position:fixed;z-index:999999;pointer-events:none;display:none;' +
      'padding:3px 8px;border-radius:4px;font-size:11px;font-family:monospace;' +
      'background:rgba(30,30,30,.88);color:#fff;white-space:nowrap;line-height:1.4;' +
      'box-shadow:0 2px 6px rgba(0,0,0,.25);';
    body.appendChild(tooltip);

    // Block-highlight style: outlines the hovered element like browser DevTools
    const hlStyle = doc.createElement('style');
    hlStyle.textContent = '.quill-hover-hl{outline:2px solid rgba(59,130,246,.8)!important;outline-offset:1px!important;background-color:rgba(59,130,246,.06)!important;}';
    body.appendChild(hlStyle);

    const herFixItChip = doc.createElement('div');
    herFixItChip.style.cssText = [
      'position:fixed', 'z-index:1000001', 'pointer-events:auto', 'display:none',
      'padding:4px 10px', 'border-radius:4px', 'font-size:11px',
      'font-family:-apple-system,BlinkMacSystemFont,sans-serif',
      'background:rgba(33,33,33,0.88)', 'color:#fff', 'cursor:pointer',
      'box-shadow:0 2px 8px rgba(0,0,0,.32)', 'user-select:none', 'line-height:1.5',
      'white-space:nowrap', 'transition:background 0.12s,color 0.12s',
    ].join(';');
    herFixItChip.appendChild(doc.createTextNode('Fix it'));
    const herChipArrow = doc.createElement('div');
    herChipArrow.style.cssText = 'position:absolute;bottom:-5px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid rgba(33,33,33,0.88);pointer-events:none';
    herFixItChip.appendChild(herChipArrow);
    body.appendChild(herFixItChip);
    let herCurrentChipPayload: PreviewHoverPayload | null = null;

    const herShowChip = (el: Element, payload: PreviewHoverPayload) => {
      herCurrentChipPayload = payload;
      herFixItChip.style.background = 'rgba(33,33,33,0.88)';
      herFixItChip.style.color = '#fff';
      herChipArrow.style.display = 'none';
      herFixItChip.style.display = 'block';
      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const cw = herFixItChip.offsetWidth || 60;
        const ch = herFixItChip.offsetHeight || 26;
        // Anchor chip inside element at top-right corner so cursor never leaves element bounds while reaching chip
        const top = Math.min(Math.max(4, rect.top + 4), (win.innerHeight || 600) - ch - 4);
        const left = Math.min(Math.max(4, rect.right - cw - 4), (win.innerWidth || 800) - cw - 4);
        herFixItChip.style.top = `${top}px`;
        herFixItChip.style.left = `${left}px`;
      });
    };
    const herHideChip = (delay = 0) => {
      if (chipHideTimer) { clearTimeout(chipHideTimer); chipHideTimer = null; }
      if (delay > 0) {
        chipHideTimer = setTimeout(() => { chipHideTimer = null; herFixItChip.style.display = 'none'; }, delay);
      } else {
        herFixItChip.style.display = 'none';
      }
    };
    herFixItChip.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation();
      if (herCurrentChipPayload) onPreviewHoverPinRef.current?.(herCurrentChipPayload);
      herFixItChip.style.background = 'transparent';
      herFixItChip.style.color = 'transparent';
      herChipArrow.style.borderTopColor = 'transparent';
      herChipArrow.style.borderBottomColor = 'transparent';
      setTimeout(() => herHideChip(), 180);
    });

    const showTooltip = (e: MouseEvent, colorHex: string | null) => {
      if (!colorHex || !showColorTooltipRef.current) { tooltip.style.display = 'none'; return; }
      tooltip.innerHTML =
        `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;border:1px solid rgba(255,255,255,.4);background:${colorHex};vertical-align:middle;margin-right:5px;"></span>` +
        colorHex.toUpperCase();
      tooltip.style.display = 'block';
      const x = Math.min(e.clientX + 12, (win.innerWidth || 800) - 120);
      const y = Math.max(e.clientY - 28, 4);
      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y}px`;
    };

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || target === herFixItChip || target === herChipArrow || target === tooltip) {
        // Mouse entered chip zone — cancel any pending delayed hide so chip stays clickable
        if (chipHideTimer) { clearTimeout(chipHideTimer); chipHideTimer = null; }
        return;
      }

      // Block highlight: outline the exact element under cursor (DevTools-style)
      if (target !== currentHlEl && target !== body) {
        currentHlEl?.classList.remove('quill-hover-hl');
        target.classList.add('quill-hover-hl');
        currentHlEl = target;
      }

      const colorHex = getElementTextColorHex(target, win);
      showTooltip(e, colorHex);

      // Always cancel dwell for whichever element it was tracking
      if (hoverDwellTimer) { clearTimeout(hoverDwellTimer); hoverDwellTimer = null; }

      if (target === lastEmittedEl) {
        pendingEl = null;
        if (herCurrentChipPayload && herFixItChip.style.display === 'none') herShowChip(target, herCurrentChipPayload);
        return;
      }

      const hints = extractDataAttrHints(target, body);
      if (!hints) { herHideChip(150); return; }

      herHideChip(150); // delayed hide — gives user time to reach chip before it disappears

      hoverDwellTimer = setTimeout(() => {
        hoverDwellTimer = null;

        if (hoverThrottleTimer) {
          pendingEl = target;
          return;
        }

        lastEmittedEl = target;
        pendingEl = null;

        if (!onPreviewHoverRef.current) return;

        const herPayload: PreviewHoverPayload = {
          channel: 'HER',
          colorHex,
          backgroundHex: resolveElementBackgroundHex(target, body, win),
          source: 'her-desktop',
          dataAttrHints: hints,
        };
        onPreviewHoverRef.current(herPayload);
        herShowChip(target, herPayload);

        hoverThrottleTimer = setTimeout(() => {
          hoverThrottleTimer = null;
          const el = pendingEl;
          pendingEl = null;
          if (!el || el === lastEmittedEl || !onPreviewHoverRef.current) return;
          const pendingHints = extractDataAttrHints(el, body);
          if (!pendingHints) return;
          lastEmittedEl = el;
          const herPendingPayload: PreviewHoverPayload = {
            channel: 'HER',
            colorHex: getElementTextColorHex(el, win),
            backgroundHex: resolveElementBackgroundHex(el, body, win),
            source: 'her-desktop',
            dataAttrHints: pendingHints,
          };
          onPreviewHoverRef.current(herPendingPayload);
          herShowChip(el, herPendingPayload);
        }, 50);
      }, HOVER_DWELL_MS);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (tooltip.style.display === 'block') {
        const x = Math.min(e.clientX + 12, (win.innerWidth || 800) - 120);
        const y = Math.max(e.clientY - 28, 4);
        tooltip.style.left = `${x}px`;
        tooltip.style.top = `${y}px`;
      }
    };

    const handleMouseLeave = () => {
      currentHlEl?.classList.remove('quill-hover-hl');
      currentHlEl = null;
      lastEmittedEl = null;
      pendingEl = null;
      if (hoverDwellTimer) { clearTimeout(hoverDwellTimer); hoverDwellTimer = null; }
      if (hoverThrottleTimer) { clearTimeout(hoverThrottleTimer); hoverThrottleTimer = null; }
      if (chipHideTimer) { clearTimeout(chipHideTimer); chipHideTimer = null; }
      tooltip.style.display = 'none';
      herCurrentChipPayload = null;
      herHideChip();
      onPreviewHoverRef.current?.(null);
    };

    body.addEventListener('mouseover', handleMouseOver);
    body.addEventListener('mousemove', handleMouseMove);
    body.addEventListener('mouseleave', handleMouseLeave);

    cleanupRef.current = () => {
      body.removeEventListener('mouseover', handleMouseOver);
      body.removeEventListener('mousemove', handleMouseMove);
      body.removeEventListener('mouseleave', handleMouseLeave);
      body.removeEventListener('click', handleClick);
      if (hoverDwellTimer) clearTimeout(hoverDwellTimer);
      if (hoverThrottleTimer) clearTimeout(hoverThrottleTimer);
      if (chipHideTimer) clearTimeout(chipHideTimer);
      currentHlEl?.classList.remove('quill-hover-hl');
      currentHlEl = null;
      tooltip.remove();
      hlStyle.remove();
      herFixItChip.remove();
      herCurrentChipPayload = null;
      herHideChip();
      lastEmittedEl = null;
      onPreviewHoverRef.current?.(null);
    };
  }, []);

  // Re-attach when hover toggle changes (listener closure reads via refs, but
  // we still need to run handleLoad to add/remove the listeners themselves).
  React.useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) return;
    handleLoad();
  }, [handleLoad, hoverInvestigateEnabled, iframeKey]);

  const scale = containerWidth > 0 ? containerWidth / contentWidth : 1;
  const scaledHeight = Math.round(iframeHeight * scale);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: `${scaledHeight}px`,
        minHeight: '400px',
        bgcolor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        // Hide until ResizeObserver fires so the 1280px iframe never flashes
        // at full (unscaled) width before the first measurement.
        visibility: containerWidth > 0 ? 'visible' : 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: HER_CAPTURE_WIDTH,
          height: iframeHeight,
          transformOrigin: 'top left',
          transform: `scale(${scale})`,
        }}
      >
        <iframe
          key={iframeKey}
          ref={iframeRef}
          srcDoc={srcDoc}
          title="HER Preview"
          sandbox="allow-same-origin"
          onLoad={handleLoad}
          style={{
            width: HER_CAPTURE_WIDTH,
            height: iframeHeight,
            border: 'none',
            backgroundColor: '#ffffff',
            display: 'block',
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * Builds a full HTML document string for rendering email content in an iframe
 * with a mobile viewport. If input has <html>/<body>, extracts body content
 * to avoid nested documents.
 */
function buildEmailMobileDocument(html: string, viewportWidth: number): string {
  if (!html?.trim()) {
    const meta = `<meta charset="UTF-8"><meta name="viewport" content="width=${viewportWidth}, initial-scale=1, maximum-scale=1, user-scalable=no">`;
    return `<!DOCTYPE html><html><head>${meta}</head><body></body></html>`;
  }
  const trimmed = html.trim();
  const looksLikeFullDocument =
    trimmed.toLowerCase().startsWith('<!doctype') || trimmed.toLowerCase().startsWith('<html');
  let bodyContent: string;
  let originalHeadStyles = '';
  if (looksLikeFullDocument) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      bodyContent = doc.body?.innerHTML ?? doc.documentElement?.innerHTML ?? html;
      originalHeadStyles = Array.from(doc.head?.querySelectorAll('style, link[rel="stylesheet"]') ?? [])
        .map(el => el.outerHTML)
        .join('');
    } catch {
      bodyContent = html;
    }
  } else {
    bodyContent = html;
  }
  const meta = `<meta charset="UTF-8"><meta name="viewport" content="width=${viewportWidth}, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover">`;
  const style = `<style>${EMAIL_MOBILE_CSS}</style>`;
  return `<!DOCTYPE html><html><head>${meta}${originalHeadStyles}${style}</head><body>${bodyContent}</body></html>`;
}

function measureEmailMobileContentHeight(doc: Document): number {
  const body = doc.body;
  const root = doc.documentElement;
  const win = doc.defaultView;

  if (!body || !root) {
    return MOBILE_PHONE.height;
  }

  const bodyRect = body.getBoundingClientRect();
  let contentBottom = 0;

  try {
    const range = doc.createRange();
    range.selectNodeContents(body);
    const rect = range.getBoundingClientRect();
    if (Number.isFinite(rect.bottom) && rect.bottom > bodyRect.top) {
      contentBottom = rect.bottom - bodyRect.top;
    }
  } catch {
    // Fall through to element-based measurement.
  }

  if (contentBottom <= 0 && win) {
    for (const el of Array.from(body.querySelectorAll('*'))) {
      const style = win.getComputedStyle(el);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.position === 'fixed' ||
        Number(style.opacity || '1') === 0
      ) {
        continue;
      }

      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 && rect.height <= 0) {
        continue;
      }

      contentBottom = Math.max(contentBottom, rect.bottom - bodyRect.top);
    }
  }

  const paddingBottom = win ? parseFloat(win.getComputedStyle(body).paddingBottom || '0') || 0 : 0;
  return Math.max(
    MOBILE_PHONE.height,
    Math.ceil(contentBottom + paddingBottom),
    body.clientHeight,
    root.clientHeight,
  );
}

async function captureEmailMobileIframeToPng(
  iframe: HTMLIFrameElement,
  fetchImage?: (src: string) => Promise<string | null>,
): Promise<Blob> {
  const doc = iframe.contentDocument;
  const root = doc?.documentElement;
  const body = doc?.body;

  if (!doc || !root || !body) {
    throw new Error('iframe document not accessible');
  }

  const targetHeight = measureEmailMobileContentHeight(doc);
  const restore = {
    rootHeight: root.style.height,
    rootMinHeight: root.style.minHeight,
    rootMaxHeight: root.style.maxHeight,
    rootOverflowY: root.style.overflowY,
    bodyHeight: body.style.height,
    bodyMinHeight: body.style.minHeight,
    bodyMaxHeight: body.style.maxHeight,
    bodyOverflowY: body.style.overflowY,
  };

  root.style.height = `${targetHeight}px`;
  root.style.minHeight = `${targetHeight}px`;
  root.style.maxHeight = `${targetHeight}px`;
  root.style.overflowY = 'hidden';

  body.style.height = `${targetHeight}px`;
  body.style.minHeight = `${targetHeight}px`;
  body.style.maxHeight = `${targetHeight}px`;
  body.style.overflowY = 'hidden';

  try {
    return await captureElementToPng(body, fetchImage);
  } finally {
    root.style.height = restore.rootHeight;
    root.style.minHeight = restore.rootMinHeight;
    root.style.maxHeight = restore.rootMaxHeight;
    root.style.overflowY = restore.rootOverflowY;

    body.style.height = restore.bodyHeight;
    body.style.minHeight = restore.bodyMinHeight;
    body.style.maxHeight = restore.bodyMaxHeight;
    body.style.overflowY = restore.bodyOverflowY;
  }
}

/** Email mobile preview: iPhone 17 Pro Max frame with iframe rendering displayHtml. */
const EmailMobilePreview: React.FC<{
  html: string;
  captureRef?: React.RefObject<(() => Promise<Blob | null>) | null>;
  fetchImage?: (src: string) => Promise<string | null>;
}> = ({ html, captureRef, fetchImage }) => {
  const theme = useTheme();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [scale, setScale] = React.useState(1);
  const [iframeKey, setIframeKey] = React.useState(0);

  React.useEffect(() => {
    if (!captureRef) return;
    captureRef.current = async () => {
      const iframe = iframeRef.current;
      if (!iframe) return null;
      try { return await captureEmailMobileIframeToPng(iframe, fetchImage); } catch { return null; }
    };
    return () => { captureRef.current = null; };
  }, [captureRef, fetchImage]);

  const srcdoc = React.useMemo(
    () => buildEmailMobileDocument(html, MOBILE_PHONE.width),
    [html]
  );

  // Force iframe remount on new content so stale DOM state is discarded
  React.useEffect(() => {
    setIframeKey(k => k + 1);
  }, [srcdoc]);

  const frameW = MOBILE_PHONE.width + MOBILE_PHONE.bezel * 2;
  const frameH = MOBILE_PHONE.height + MOBILE_PHONE.bezel * 2;

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateScale = () => {
      const { clientWidth, clientHeight } = el;
      const s = Math.min(clientWidth / frameW, clientHeight / frameH, MAX_PHONE_SCALE);
      setScale(Math.max(0.2, s));
    };
    updateScale();
    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScale);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [frameW, frameH]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Wrapper with scaled dimensions for layout—transform:scale() does not reduce layout space */}
      <Box
        sx={{
          width: frameW * scale,
          height: frameH * scale,
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: frameW,
            height: frameH,
            transform: `translate(-50%, -50%) scale(${scale})`,
            transformOrigin: 'center center',
            bgcolor: '#000',
            border: `${MOBILE_PHONE.bezel}px solid #000`,
            borderRadius: '44px',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 20px 25px rgba(0,0,0,0.6)'
                : '0 20px 25px rgba(0,0,0,0.4)',
            overflow: 'hidden',
          }}
        >
        <Box
          sx={{
            width: MOBILE_PHONE.width,
            height: MOBILE_PHONE.height,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Dynamic Island - iPhone 17 Pro Max style */}
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 28,
              borderRadius: 14,
              bgcolor: '#000',
              zIndex: 2,
            }}
          />
          <iframe
            ref={iframeRef}
            key={iframeKey}
            title="Email mobile preview"
            srcDoc={srcdoc}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: '#fff',
              display: 'block',
            }}
          />
        </Box>
        </Box>
      </Box>
    </Box>
  );
};

type VariantConfig = {
  templateKey?: string;
  hierarchy?: string;
  locale?: string;
  scenario?: string;
};

type PreviewFrameProps = {
  variantName?: string;
  variant?: VariantConfig | null;
  renderRequest: RenderRequest | null;
  previewMode: 'desktop' | 'mobile';
  channel?: string;
  desktopPreviewMaxWidth?: number;
  fetchImage?: (src: string) => Promise<string | null>;
  isFullTemplateSelected?: boolean;
  renderedHtml?: string;
  renderedSubject?: string;
  loading?: boolean;
  isWebWidgetTemplate?: boolean;
  herPreviewHtml?: string | null; // HER preview HTML to display in iframe via srcdoc
  onWebWidgetFinished?: (templateKey: string, result: { success: boolean; error?: string }) => void;
  onPreviewHover?: (payload: PreviewHoverPayload | null) => void;
  onPreviewHoverPin?: (payload: PreviewHoverPayload) => void;
  hoverInvestigateEnabled?: boolean;
  showColorTooltip?: boolean;
};

export const PreviewFrame = React.forwardRef<PreviewExportHandle, PreviewFrameProps>(({
  variantName,
  variant,
  renderRequest,
  previewMode,
  channel = 'Email',
  desktopPreviewMaxWidth = 1024,
  isFullTemplateSelected = false,
  renderedHtml,
  renderedSubject,
  loading = false,
  isWebWidgetTemplate = false,
  herPreviewHtml,
  onWebWidgetFinished,
  onPreviewHover,
  onPreviewHoverPin,
  hoverInvestigateEnabled = false,
  showColorTooltip = false,
  fetchImage,
}, ref) => {
  const theme = useTheme();
  const effectiveTemplateKey = variant?.templateKey || renderRequest?.templateKey;
  const desktopPreviewWidth = channel === 'Web' ? undefined : desktopPreviewMaxWidth;
  const refreshKey = renderRequest?.refreshKey ?? null; // Extract refreshKey to trigger re-render on save
  const effectiveElementKey = renderRequest?.elementKey || null;
  const { instanceId, instanceIdRef, isInitializing, initError, isReady, reinitialize, sdkScriptUrl, getIframeInitConfig } = useWidgetSdk();

  const widgetContainerRef = React.useRef<HTMLDivElement | null>(null);

  // Capture function refs populated by each active preview sub-component.
  const emailDesktopCaptureRef = React.useRef<(() => Promise<Blob | null>) | null>(null);
  const herCaptureRef = React.useRef<(() => Promise<Blob | null>) | null>(null);
  const emailMobileCaptureRef = React.useRef<(() => Promise<Blob | null>) | null>(null);
  const webMobileCaptureRef = React.useRef<(() => Promise<Blob | null>) | null>(null);

  React.useImperativeHandle(ref, () => ({
    exportPreview: async (action: ExportAction): Promise<ExportResult> => {
      if (loading) return { ok: false, reason: 'not-ready' };

      const filename = buildExportFilename(
        effectiveTemplateKey ?? 'preview',
        channel ?? 'email',
        previewMode,
      );

      // Web desktop — capture widget container div directly.
      if (channel === 'Web' && isWebWidgetTemplate && previewMode === 'desktop') {
        const container = widgetContainerRef.current;
        if (!container || !isReady) return { ok: false, reason: 'not-ready' };
        return performExport(action, () => captureElementToPng(container, fetchImage), filename);
      }

      // Web mobile — same-origin iframe, capture via contentDocument.
      if (channel === 'Web' && isWebWidgetTemplate && previewMode === 'mobile') {
        const fn = webMobileCaptureRef.current;
        if (!fn) return { ok: false, reason: 'not-ready' };
        return performExport(action, async () => {
          const blob = await fn();
          if (!blob) throw new Error('capture returned null');
          return blob;
        }, filename);
      }

      // HER desktop.
      if (channel === 'HER') {
        const fn = herCaptureRef.current;
        if (!fn) return { ok: false, reason: 'not-ready' };
        return performExport(action, async () => {
          const blob = await fn();
          if (!blob) throw new Error('capture returned null');
          return blob;
        }, filename);
      }

      // Email mobile.
      if (previewMode === 'mobile') {
        const fn = emailMobileCaptureRef.current;
        if (!fn) return { ok: false, reason: 'not-ready' };
        return performExport(action, async () => {
          const blob = await fn();
          if (!blob) throw new Error('capture returned null');
          return blob;
        }, filename);
      }

      // Email desktop (default).
      const fn = emailDesktopCaptureRef.current;
      if (!fn) return { ok: false, reason: 'not-ready' };
      return performExport(action, async () => {
        const blob = await fn();
        if (!blob) throw new Error('capture returned null');
        return blob;
      }, filename);
    },
  }), [loading, channel, isWebWidgetTemplate, previewMode, isReady, effectiveTemplateKey]);
  // Important: this page can render multiple widgets at once (e.g., 5 predefined widgets).
  // A static id like "widget-container" will collide and can break SDKs that query by element id.
  const reactId = React.useId();
  const widgetContainerDomId = React.useMemo(() => {
    // useId() can include ":" which is valid in HTML but can be awkward in selectors; normalize it.
    const rid = reactId.replace(/[:]/g, '');
    return `widget-container-${rid}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactId]);
  const [selectedWidgetTag, setSelectedWidgetTag] = React.useState<string | null>(null);
  const errorToastShownRef = React.useRef<string | null>(null); // Track which template we've shown error for
  const onPreviewHoverRef = React.useRef(onPreviewHover);
  const showColorTooltipRef = React.useRef(showColorTooltip);

  React.useEffect(() => {
    onPreviewHoverRef.current = onPreviewHover;
  }, [onPreviewHover]);

  React.useEffect(() => {
    showColorTooltipRef.current = showColorTooltip;
  }, [showColorTooltip]);

  // Resolve widget tag(s) for Web mobile iframe (SDK must be loaded so WIDGET_TAG is available).
  // Returns string for single widget, or array of strings for multi-widget templates
  const webMobileWidgetTag = React.useMemo(() => {
    if (!effectiveTemplateKey || typeof window === 'undefined') return null;
    const key = getWidgetIdFromTemplate(effectiveTemplateKey);
    const tags = (window as any).BidgelyWebSdk?.WIDGET_TAG as Record<string, string> | undefined;

    // For multi-widget templates, return array of all widget tags
    if (Array.isArray(key)) {
      if (!tags) return null;
      const resolvedTags = key.map(k => tags[k]).filter(Boolean);
      return resolvedTags.length > 0 ? resolvedTags : null;
    }

    return key && tags ? tags[key] ?? null : null;
  }, [effectiveTemplateKey, isReady]); // isReady so we recompute when SDK loads
  
  // Track template changes, refreshKey, scenario, and previewMode for re-initialization
  const prevTemplateKeyRef = React.useRef<string | null>(null);
  const prevRefreshKeyRef = React.useRef<number | null>(null);
  const prevScenarioIdRef = React.useRef<string | null>(null);
  const prevPreviewModeRef = React.useRef<'desktop' | 'mobile' | null>(null);
  /** When true, we have started init+render (reinitialize then render); skip effect re-runs until that call settles to avoid double init/render. */
  const initRenderInProgressRef = React.useRef(false);
  // Stable ref for reinitialize so the render effect doesn't re-run when sdkLoaded/sdkLoading
  // transitions cause reinitialize's useCallback reference to change. Without this, every SPA
  // navigation triggers a spurious cleanup+re-run that briefly clears the widget container.
  const reinitializeRef = React.useRef(reinitialize);
  React.useEffect(() => { reinitializeRef.current = reinitialize; }, [reinitialize]);

  // Get data from Redux
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  
  // Extract fuelType from projectAttributes
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);

  const locale = variant?.locale || renderRequest?.variant?.locale || 'en_US';

  // Extract fuelType from renderRequest (for Web templates) or use default
  const requestFuelType = renderRequest?.fuelType;

  // Get scenarios from Redux
  const availableScenarios = useSelector((state: RootState) => {
    if (!effectiveTemplateKey || !selectedPilotId) {
      return [];
    }

    // Use request fuel type if available (from Web templates), otherwise use default fuel type
    const effectiveFuelType = requestFuelType || fuelType;

    console.log('[PreviewFrame] Fetching scenarios from Redux:', {
      templateKey: effectiveTemplateKey,
      effectiveFuelType,
      requestFuelType,
      fuelType,
      locale,
      channel,
      selectedPilotId
    });

    const scenarios = selectScenariosForTemplate(
      state,
      String(selectedPilotId),
      effectiveFuelType,
      locale,
      effectiveTemplateKey, // Clean template key
      channel
    );

    console.log('[PreviewFrame] Scenarios fetched from Redux:', {
      count: scenarios?.length || 0,
      scenarios: scenarios?.map(s => ({ scenarioId: s.scenarioId, templateName: s.templateName, fuelType: s.fuelType }))
    });

    return scenarios;
  });

  // Resolve scenario ID: always return an actual scenarioId from DataScenario objects
  // Note: 'default' is just a display name, not a scenarioId
  // availableScenarios are already filtered by template and productType (channel) in the selector
  const resolvedScenarioId = React.useMemo(() => {
    const scenario = variant?.scenario || renderRequest?.variant?.scenario;

    console.log('[PreviewFrame] Resolving scenario:', {
      effectiveTemplateKey,
      requestFuelType,
      scenario,
      availableScenariosCount: availableScenarios?.length || 0,
      availableScenarios: availableScenarios?.map(s => ({ scenarioId: s.scenarioId, templateName: s.templateName }))
    });

    // Special-case: "Web_FullTemplate_1" is a cross-template Web scenario used for rendering multiple widgets
    // (e.g., Color Palette "View All Web Widgets"). It may not appear under each widget's templateName in scenarios,
    // so we must allow it even if it's not present in availableScenarios for the specific widget template.
    if (channel === 'Web' && scenario === 'Web_FullTemplate_1') {
      return scenario;
    }

    // For any channel (including Web): only use an explicitly provided scenario
    // if it exists in the scenarios list for this template. Otherwise, fall back
    // to the template's default scenario to avoid crashing the widget runtime.
    if (scenario && scenario !== 'default') {
      const foundScenario = availableScenarios?.find(ds => ds.scenarioId === scenario);
      if (foundScenario) {
        return foundScenario.scenarioId;
      }
      // Fall through to default scenario resolution
    }

    // If no scenarios available, we cannot resolve a scenarioId
    if (!availableScenarios || availableScenarios.length === 0) {
      console.error('[PreviewFrame] No scenarios available - cannot resolve scenarioId');
      return null; // Return null instead of 'default' string
    }

    // Note: scenario override (if present) is handled above with a safe existence check.

    // Find default scenario: first try defaultScenario flag, then scenarioDisplayName "Default", then first scenario
    // This handles both "default" string and when no scenario is provided
    const defaultScenario = availableScenarios.find(ds => ds.defaultScenario === true) ||
                           availableScenarios.find(ds => ds.scenarioDisplayName === 'Default') ||
                           availableScenarios[0];

    if (defaultScenario) {
      return defaultScenario.scenarioId; // Always return actual scenarioId, never 'default' string
    }

    // This should never happen if availableScenarios has items
    console.error('[PreviewFrame] Could not find any scenario in availableScenarios');
    return null;
  }, [variant?.scenario, renderRequest?.variant?.scenario, availableScenarios, channel, effectiveTemplateKey, requestFuelType]);

  // Get loading state to avoid showing error while scenarios are still loading
  const scenariosLoading = useSelector((state: RootState) => {
    if (!selectedPilotId || !effectiveTemplateKey) return false;

    // Use request fuel type if available
    const effectiveFuelType = requestFuelType || fuelType;

    return selectDataScenariosLoading(
      state,
      String(selectedPilotId),
      effectiveFuelType,
      locale
    );
  });

  // Show error toast when no data scenario is found for Web widget
  // Use a delayed approach to avoid showing toast during rapid state changes
  React.useEffect(() => {
    // Only show error for Web widgets when:
    // 1. Template is selected
    // 2. Scenarios are not loading
    // 3. SDK is ready (to avoid showing during initial load)
    // 4. No scenarios available
    if (
      channel === 'Web' &&
      isWebWidgetTemplate &&
      effectiveTemplateKey &&
      !scenariosLoading &&
      isReady && // Only show when SDK is ready
      (!availableScenarios || availableScenarios.length === 0)
    ) {
      // Only show toast once per template to avoid spam
      if (errorToastShownRef.current !== effectiveTemplateKey) {
        // Add a delay to avoid showing toast during rapid state changes or initial load
        // This ensures we only show the error when the user is actually trying to view the widget
        const timeoutId = setTimeout(() => {
          // Double-check conditions after delay to ensure we still need to show the error
          if (
            channel === 'Web' &&
            isWebWidgetTemplate &&
            effectiveTemplateKey &&
            !scenariosLoading &&
            isReady &&
            (!availableScenarios || availableScenarios.length === 0) &&
            errorToastShownRef.current !== effectiveTemplateKey
          ) {
        errorToastShownRef.current = effectiveTemplateKey;
            // Use warning instead of error to be less aggressive
            toast.warning(
              `No data scenario found for widget "${effectiveTemplateKey}". Please configure a data scenario in the Data Scenarios section.`
        );
          }
        }, 2000); // Wait 2 seconds before showing toast to ensure stable state

        return () => {
          clearTimeout(timeoutId);
        };
      }
    } else if (availableScenarios && availableScenarios.length > 0) {
      // Reset the ref when scenarios become available
      errorToastShownRef.current = null;
    }
  }, [
    channel,
    isWebWidgetTemplate,
    effectiveTemplateKey,
    availableScenarios,
    scenariosLoading,
    isReady, // Add isReady to dependencies
  ]);

  // Render widget when SDK is ready and template is selected
  React.useEffect(() => {
    if (channel !== 'Web' || !isWebWidgetTemplate) {
      setSelectedWidgetTag(null);
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      return;
    }

    if (!effectiveTemplateKey || !isReady || !instanceId || !resolvedScenarioId) {
      return;
    }

    // Skip if we already started an init+render (reinitialize then render): reinitialize() updates instanceId state and re-triggers this effect; we must not start a second init+render.
    if (initRenderInProgressRef.current) {
      return;
    }

    // Re-initialize whenever we need to render with fresh context: template, refreshKey, scenario, or preview mode. Init and render are mandatory together (always init before render).
    const templateChanged = effectiveTemplateKey !== prevTemplateKeyRef.current && prevTemplateKeyRef.current !== null;
    const refreshKeyChanged = refreshKey !== null && refreshKey !== prevRefreshKeyRef.current && prevRefreshKeyRef.current !== null;
    const scenarioChanged = prevScenarioIdRef.current !== null && prevScenarioIdRef.current !== resolvedScenarioId;
    const previewModeChanged = prevPreviewModeRef.current !== null && prevPreviewModeRef.current !== previewMode;
    const shouldReinitialize = templateChanged || refreshKeyChanged || scenarioChanged || previewModeChanged;

    // Web mobile uses iframe-based preview; widget is rendered inside the iframe. Trigger reinitialize with selected scenario so init uses same scenario (single uiConfigs call).
    if (previewMode === 'mobile') {
      if (shouldReinitialize) {
        // Use request fuel type for mobile preview
        reinitializeRef.current(resolvedScenarioId, requestFuelType || undefined).catch(err => console.error('[PreviewFrame] Re-initialize for mobile failed:', err));
      }
      return;
    }

    if (!widgetContainerRef.current) {
      return;
    }

    // Prevent duplicate renders for the same widget + scenario + instance + refreshKey in dev StrictMode
    const renderSignature = `${effectiveTemplateKey}|${instanceId}|${resolvedScenarioId}|${String(refreshKey ?? '')}`;
    const alreadyRendered =
      widgetContainerRef.current &&
      widgetContainerRef.current.childNodes &&
      widgetContainerRef.current.childNodes.length > 0;
    if (prevScenarioIdRef.current === resolvedScenarioId && prevTemplateKeyRef.current === effectiveTemplateKey && prevRefreshKeyRef.current === refreshKey && alreadyRendered) {
      return;
    }

    // Mandatory: init then render together. When we reinitialize, mark in progress so effect re-runs (from instanceId updates) don't start a second init+render.
    if (shouldReinitialize) {
      initRenderInProgressRef.current = true;
    }

    // Check if this is a multi-widget template
    const isMultiWidget = isMultiWidgetTemplate(effectiveTemplateKey);

    // Use appropriate renderer based on template type
    const renderPromise = isMultiWidget
      ? initializeAndRenderMultipleWidgets({
          templateKey: effectiveTemplateKey,
          scenarioId: resolvedScenarioId,
          instanceId: instanceId,
          instanceIdRef: shouldReinitialize ? instanceIdRef : undefined,
          containerRef: widgetContainerRef,
          reinitialize: shouldReinitialize ? () => reinitializeRef.current(resolvedScenarioId, requestFuelType || undefined) : undefined,
          onRenderComplete: (response) => {
            console.log('[PreviewFrame] Multi-widget render complete:', response);
          },
          onError: (error) => {
            console.error('[PreviewFrame] Multi-widget render error:', error);
            if (widgetContainerRef.current) {
              widgetContainerRef.current.innerHTML = '';
            }
          }
        })
      : initializeAndRenderWidget({
          templateKey: effectiveTemplateKey, // Clean template key
          scenarioId: resolvedScenarioId,
          instanceId: instanceId,
          instanceIdRef: shouldReinitialize ? instanceIdRef : undefined,
          containerRef: widgetContainerRef,
          reinitialize: shouldReinitialize ? () => reinitializeRef.current(resolvedScenarioId, requestFuelType || undefined) : undefined,
          onRenderComplete: (response) => {
            // Get widget tag for state tracking
            const widgetKey = getWidgetIdFromTemplate(effectiveTemplateKey);
            if (widgetKey && !Array.isArray(widgetKey) && window.BidgelyWebSdk?.WIDGET_TAG?.[widgetKey]) {
              setSelectedWidgetTag(window.BidgelyWebSdk.WIDGET_TAG[widgetKey]);
            }
          },
          onError: (error) => {
            console.error('[PreviewFrame] Widget render error:', error);
            // Clear container on error
            if (widgetContainerRef.current) {
              widgetContainerRef.current.innerHTML = '';
            }
          }
        });

    renderPromise
      .then((result) => {
        if (result.success) {
          if (result.widgetTag) {
            setSelectedWidgetTag(result.widgetTag);
          } else if (result.widgetTags && result.widgetTags.length > 0) {
            // For multi-widget, set the first widget tag
            setSelectedWidgetTag(result.widgetTags[0]);
          }
        }
        if (onWebWidgetFinished && effectiveTemplateKey) {
          onWebWidgetFinished(effectiveTemplateKey, { success: result.success, error: result.error });
        }
      })
      .catch((error) => {
        console.error('[PreviewFrame] Fatal error in widget rendering:', error);
        if (onWebWidgetFinished && effectiveTemplateKey) {
          onWebWidgetFinished(effectiveTemplateKey, { success: false, error: error?.message || String(error || '') });
        }
      })
      .finally(() => {
        initRenderInProgressRef.current = false;
      });

    // Cleanup function to remove widget on unmount or template change
    return () => {
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      setSelectedWidgetTag(null);
    };
  }, [channel, isWebWidgetTemplate, effectiveTemplateKey, isReady, instanceId, resolvedScenarioId, refreshKey, previewMode]); // previewMode: re-render widget when switching desktop/mobile so the correct container (ref) gets the widget

  React.useEffect(() => {
    const isWebDesktop =
      hoverInvestigateEnabled &&
      channel === 'Web' &&
      isWebWidgetTemplate &&
      previewMode === 'desktop';
    if (!isWebDesktop) {
      onPreviewHoverRef.current?.(null);
      return;
    }

    const container = widgetContainerRef.current;
    const doc = container?.ownerDocument;
    const win = doc?.defaultView;
    if (!container || !doc || !win) return;

    let hoverThrottleTimer: ReturnType<typeof setTimeout> | null = null;
    let lastEmittedEl: Element | null = null;
    let pendingEmit: (() => void) | null = null;
    let debugTargetEl: Element | null = null;
    const iframeCleanupMap = new Map<HTMLIFrameElement, () => void>();

    const tooltip = doc.createElement('div');
    tooltip.style.cssText =
      'position:fixed;z-index:999999;pointer-events:none;display:none;' +
      'padding:3px 8px;border-radius:4px;font-size:11px;font-family:monospace;' +
      'background:rgba(30,30,30,.88);color:#fff;white-space:nowrap;line-height:1.4;' +
      'box-shadow:0 2px 6px rgba(0,0,0,.25);';
    doc.body.appendChild(tooltip);

    // Block-highlight style for Web widget hover target (same visual as email/HER)
    const hlStyle = doc.createElement('style');
    hlStyle.textContent = '.quill-hover-debug-target{outline:2px solid rgba(59,130,246,.8)!important;outline-offset:1px!important;background-color:rgba(59,130,246,.06)!important;}';
    doc.body.appendChild(hlStyle);

    const positionTooltip = (x: number, y: number) => {
      const safeX = Math.min(x + 12, (win.innerWidth || 800) - 120);
      const safeY = Math.max(y - 28, 4);
      tooltip.style.left = `${safeX}px`;
      tooltip.style.top = `${safeY}px`;
    };

    const showTooltip = (x: number, y: number, colorHex: string | null) => {
      if (!colorHex || !showColorTooltipRef.current) {
        tooltip.style.display = 'none';
        return;
      }
      tooltip.innerHTML =
        `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;border:1px solid rgba(255,255,255,.4);background:${colorHex};vertical-align:middle;margin-right:5px;"></span>` +
        colorHex.toUpperCase();
      tooltip.style.display = 'block';
      positionTooltip(x, y);
    };

    const clearHover = () => {
      lastEmittedEl = null;
      tooltip.style.display = 'none';
      debugTargetEl?.classList.remove('quill-hover-debug-target');
      debugTargetEl = null;
      onPreviewHoverRef.current?.(null);
    };

    const emitHoverForElement = (
      el: Element,
      boundary: Element,
      sourceWin: Window,
      tooltipX: number,
      tooltipY: number,
    ) => {
      if (debugTargetEl !== el) {
        debugTargetEl?.classList.remove('quill-hover-debug-target');
        el.classList.add('quill-hover-debug-target');
        debugTargetEl = el;
      }

      const colorHex = getElementTextColorHex(el, sourceWin);
      showTooltip(tooltipX, tooltipY, colorHex);

      if (el === lastEmittedEl) { pendingEmit = null; return; }

      const hints = extractDataAttrHints(el, boundary);
      if (!hints) return;

      const doEmit = () => {
        lastEmittedEl = el;
        pendingEmit = null;
        onPreviewHoverRef.current?.({
          channel: 'Web',
          colorHex,
          backgroundHex: resolveElementBackgroundHex(el, boundary, sourceWin),
          source: 'web-desktop',
          dataAttrHints: hints,
        });
      };

      if (hoverThrottleTimer) {
        pendingEmit = doEmit;
        return;
      }

      doEmit();
      hoverThrottleTimer = setTimeout(() => {
        hoverThrottleTimer = null;
        const fn = pendingEmit;
        if (fn) fn();
      }, 50);
    };

    const attachDocumentTracking = (
      targetDoc: Document,
      boundary: Element,
      sourceWin: Window,
      toViewportCoords: (x: number, y: number) => { x: number; y: number },
    ) => {
      const handlePointerMove = (e: PointerEvent) => {
        // `getNonPopupElementFromPoint` skips transient overlays (tooltips,
        // appliance-detail popups, etc.) that widget JS opens on hover and
        // returns the content row underneath. Without this, the Bidgely
        // Web appliance breakdown's $X-this-month popup steals the
        // elementFromPoint result so hover text reads popup copy instead
        // of the row label (e.g. "Always On") the user wants to match.
        const pathTarget = getNonPopupElementFromPoint(
          targetDoc,
          e.clientX,
          e.clientY,
          boundary,
        );
        if (!pathTarget || !isWithinBoundary(pathTarget, boundary)) {
          clearHover();
          return;
        }

        const coords = toViewportCoords(e.clientX, e.clientY);
        emitHoverForElement(pathTarget, boundary, sourceWin, coords.x, coords.y);
      };

      targetDoc.addEventListener('pointermove', handlePointerMove, true);

      return () => {
        targetDoc.removeEventListener('pointermove', handlePointerMove, true);
      };
    };

    const attachIframeTracking = (iframe: HTMLIFrameElement) => {
      let detachInner: (() => void) | null = null;

      const attachInner = () => {
        detachInner?.();
        try {
          const iframeDoc = iframe.contentDocument;
          const iframeWin = iframe.contentWindow;
          const iframeBoundary = iframeDoc?.body || iframeDoc?.documentElement;
          if (!iframeDoc || !iframeWin || !iframeBoundary) return;

          detachInner = attachDocumentTracking(
            iframeDoc,
            iframeBoundary,
            iframeWin,
            (x, y) => {
              const rect = iframe.getBoundingClientRect();
              return { x: rect.left + x, y: rect.top + y };
            },
          );
        } catch {
          // Cross-origin iframe: cannot access its document for hover tracking.
        }
      };

      const handleLoad = () => {
        attachInner();
      };

      iframe.addEventListener('load', handleLoad);
      attachInner();

      return () => {
        iframe.removeEventListener('load', handleLoad);
        detachInner?.();
      };
    };

    const syncIframeTracking = () => {
      const iframes = Array.from(container.querySelectorAll('iframe'));
      const nextSet = new Set(iframes);

      iframeCleanupMap.forEach((cleanup, iframe) => {
        if (!nextSet.has(iframe)) {
          cleanup();
          iframeCleanupMap.delete(iframe);
        }
      });

      iframes.forEach((iframe) => {
        if (!iframeCleanupMap.has(iframe)) {
          iframeCleanupMap.set(iframe, attachIframeTracking(iframe));
        }
      });
    };

    const topDocCleanup = attachDocumentTracking(
      doc,
      container,
      win,
      (x, y) => ({ x, y }),
    );

    const handleContainerLeave = () => {
      clearHover();
    };

    container.addEventListener('pointerleave', handleContainerLeave, true);

    const mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            syncIframeTracking();
          })
        : null;
    mutationObserver?.observe(container, { childList: true, subtree: true });
    syncIframeTracking();

    return () => {
      mutationObserver?.disconnect();
      topDocCleanup();
      container.removeEventListener('pointerleave', handleContainerLeave, true);
      iframeCleanupMap.forEach((cleanup) => cleanup());
      iframeCleanupMap.clear();
      if (hoverThrottleTimer) clearTimeout(hoverThrottleTimer);
      tooltip.remove();
      hlStyle.remove();
      clearHover();
    };
  }, [channel, isWebWidgetTemplate, previewMode, effectiveTemplateKey, resolvedScenarioId, refreshKey, hoverInvestigateEnabled]);
  
  // Track template, refreshKey, scenario, and previewMode to update refs (for determining when to re-initialize)
  React.useEffect(() => {
    if (effectiveTemplateKey && effectiveTemplateKey !== prevTemplateKeyRef.current) {
      prevTemplateKeyRef.current = effectiveTemplateKey;
    }
    if (refreshKey !== null && refreshKey !== prevRefreshKeyRef.current) {
      prevRefreshKeyRef.current = refreshKey;
    }
    if (resolvedScenarioId && resolvedScenarioId !== prevScenarioIdRef.current) {
      prevScenarioIdRef.current = resolvedScenarioId;
    }
    prevPreviewModeRef.current = previewMode;
  }, [effectiveTemplateKey, refreshKey, resolvedScenarioId, previewMode]);

  const webWidgetDisplayName = React.useMemo(
    () => getWebWidgetDisplayName(effectiveTemplateKey ?? null),
    [effectiveTemplateKey]
  );

  // The API may return the subject as HTML (e.g. "<p>text</p><p></p>").
  // Strip tags so the subject card shows plain text, not raw markup.
  const subjectLine = renderedSubject
    ? renderedSubject.replace(/<[^>]*>/g, '').trim() || null
    : null;
  const isTeaserSection = channel === 'Email' && String(effectiveElementKey || '').toUpperCase() === 'TEASER';
  const teaserText = React.useMemo(
    () => (isTeaserSection ? extractEmailTeaserText(renderedHtml || '') : null),
    [isTeaserSection, renderedHtml]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
      {!renderRequest && (
        <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">Select a template to preview</Typography>
        </Box>
      )}
      {variantName && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)',
            color: 'text.secondary',
            fontSize: '12px',
            fontWeight: 500
          }}
        >
          <Typography component="span" variant="caption">{variantName}:</Typography>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.hierarchy || 'Project'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.locale || 'en_US'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.scenario || 'default'}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <style>
          {`
            .quill-hover-debug-target {
              outline: 2px solid #ff6b35 !important;
              outline-offset: 2px !important;
              box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.18) !important;
            }
          `}
        </style>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 5,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body2">Loading preview...</Typography>
          </Box>
        )}

        {!loading && channel === 'Email' && subjectLine && isFullTemplateSelected && (
          <Card
            sx={{
              marginBottom: 2,
              boxShadow: theme => theme.palette.mode === 'dark'
                ? '0 4px 6px rgba(0,0,0,0.3)'
                : '0 4px 6px rgba(0,0,0,0.1)',
              bgcolor: 'background.paper'
            }}
          >
            <CardHeader
              sx={{ padding: 1.5 }}
              title={
                <Box
                  component="label"
                  htmlFor={`subject-line-preview-${variantName || 'main'}`}
                sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'text.secondary',
                  display: 'block',
                  marginBottom: 0.5
                }}
              >
                Subject Line
                </Box>
              }
              subheader={
                <Typography
                  id={`subject-line-preview-${variantName || 'main'}`}
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    margin: 0
                  }}
                >
                {subjectLine}
              </Typography>
              }
            />
          </Card>
        )}

        {!loading && isTeaserSection && (
          <Card
            sx={{
              margin: '0 auto',
              width: '100%',
              maxWidth: `${desktopPreviewWidth}px`,
              boxShadow: theme => theme.palette.mode === 'dark'
                ? '0 4px 6px rgba(0,0,0,0.3)'
                : '0 4px 6px rgba(0,0,0,0.1)',
              bgcolor: 'background.paper'
            }}
          >
            <CardHeader
              sx={{ padding: 1.5 }}
              title={
                <Box
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'text.secondary',
                    display: 'block',
                    marginBottom: 0.5
                  }}
                >
                  Email Preview
                </Box>
              }
              subheader={
                <Box
                  sx={{
                    marginTop: 0.5,
                    padding: '12px 14px',
                    borderRadius: 1.5,
                    border: '1px dashed',
                    borderColor: 'divider',
                    bgcolor: '#fffbe6',
                    color: 'text.primary',
                    fontSize: '14px',
                    lineHeight: 1.5
                  }}
                >
                  {teaserText || 'No email preview text found in the rendered email.'}
                </Box>
              }
            />
          </Card>
        )}

        {/* Web widget path: direct render (no iframe, same as WidgetsSdkDemoPage) */}
        {!loading && !isTeaserSection && channel === 'Web' && isWebWidgetTemplate ? (
          previewMode === 'mobile' ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <MobilePreview showPhoneFrame>
                <WidgetErrorBoundary>
                  {initError && (
                    <Box sx={{ padding: 2, textAlign: 'center', color: 'error.main' }}>
                      <Typography variant="body2">Error: {initError}</Typography>
                    </Box>
                  )}
                  {!initError && (
                    <WebWidgetMobileIframe
                      sdkScriptUrl={sdkScriptUrl}
                      scenarioId={resolvedScenarioId ?? ''}
                      widgetTag={webMobileWidgetTag}
                      iframeInitConfig={getIframeInitConfig(resolvedScenarioId ?? undefined)}
                      displayName={webWidgetDisplayName}
                      isSdkInitializing={isInitializing}
                      isSdkReady={isReady}
                      captureRef={webMobileCaptureRef}
                      fetchImage={fetchImage}
                    />
                  )}
                </WidgetErrorBoundary>
              </MobilePreview>
            </Box>
          ) : (
            <WidgetErrorBoundary>
              <Box
                sx={{
                  margin: '0 auto',
                  bgcolor: 'background.paper',
                  boxShadow: theme => theme.palette.mode === 'dark'
                    ? '0 4px 6px rgba(0,0,0,0.3)'
                    : '0 4px 6px rgba(0,0,0,0.1)',
                  overflow: 'visible',
                  borderRadius: 2,
                  width: `${getWebWidgetDesktopWidth(effectiveTemplateKey ?? null)}px`,
                  minWidth: `${getWebWidgetDesktopWidth(effectiveTemplateKey ?? null)}px`,
                  maxWidth: '100%',
                  minHeight: 400,
                  padding: 2,
                  position: 'relative'
                }}
              >
                <div
                  ref={widgetContainerRef}
                  id={widgetContainerDomId}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '400px'
                  }}
                />
                {initError ? (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2, textAlign: 'center', color: 'error.main' }}>
                    <Typography variant="body2">Error: {initError}</Typography>
                  </Box>
                ) : (!isReady || scenariosLoading || !selectedWidgetTag) && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, color: 'text.secondary', pointerEvents: 'none' }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2">
                      {isInitializing ? `Initializing ${webWidgetDisplayName}…` : `Loading ${webWidgetDisplayName}…`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </WidgetErrorBoundary>
          )
        ) : !loading && !isTeaserSection && channel === 'HER' && herPreviewHtml ? (
          // HER preview: same-origin iframe via srcDoc so hover can inspect DOM.
          <HerDesktopPreview
            srcDoc={herPreviewHtml}
            onPreviewHover={onPreviewHover}
            onPreviewHoverPin={onPreviewHoverPin}
            hoverInvestigateEnabled={hoverInvestigateEnabled}
            showColorTooltip={showColorTooltip}
            captureRef={herCaptureRef}
            fetchImage={fetchImage}
          />
        ) : !loading && !isTeaserSection && renderedHtml ? (
          // Render HTML from API - apply mobile or desktop styling based on previewMode
          previewMode === 'mobile' && channel !== 'HER' ? (
            <Box sx={{ flex: 1, minHeight: 500, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <EmailPreviewErrorBoundary>
                <EmailMobilePreview html={renderedHtml || ''} captureRef={emailMobileCaptureRef} fetchImage={fetchImage} />
              </EmailPreviewErrorBoundary>
            </Box>
          ) : (
            // Desktop preview — rendered in an iframe so global app CSS
            // (e.g. `* { font-family: Inter !important }`) cannot override the
            // email's inline font declarations, which would cause text to reflow
            // differently from what production email clients render.
            <Box
              sx={{
                margin: '0 auto',
                bgcolor: 'background.paper',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 4px 6px rgba(0,0,0,0.3)'
                  : '0 4px 6px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                borderRadius: 2,
                ...(channel === 'Web'
                  ? { minWidth: '1152px' }
                  : { maxWidth: `${desktopPreviewWidth}px` })
              }}
            >
              <EmailPreviewErrorBoundary>
                <EmailDesktopPreview
                  html={renderedHtml || ''}
                  channel={channel === 'Web' || channel === 'HER' ? channel : 'Email'}
                  onPreviewHover={onPreviewHover}
                  onPreviewHoverPin={onPreviewHoverPin}
                  hoverInvestigateEnabled={hoverInvestigateEnabled}
                  showColorTooltip={showColorTooltip}
                  captureRef={emailDesktopCaptureRef}
                  fetchImage={fetchImage}
                />
              </EmailPreviewErrorBoundary>
            </Box>
          )
        ) : !loading && !isTeaserSection && (
          // Show message if no rendered HTML available
          <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No preview available</Typography>
          </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
});
